import h from 'hyperscript';

import addViewIcon from '@disy/cadenza-icons/add-view.svg';
import analysisContextBubbleIcon from '@disy/cadenza-icons/analysis-context-bubble.svg';
import analysisContextIcon from '@disy/cadenza-icons/analysis-context.svg';
import dataBrowserIcon from '@disy/cadenza-icons/database.svg';
import designerIcon from '@disy/cadenza-icons/designer.svg';
import more from '@disy/cadenza-icons/more.svg';
import saveIcon from '@disy/cadenza-icons/save.svg';
import shareIcon from '@disy/cadenza-icons/share.svg';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { on } from '@disy/lib/event-util';
import { currentWindowMediaXsOnly } from '@disy/lib/media';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { createButton, createIconButton, updateButtonTitle } from '@disy/ui/button';
import type { Icon } from '@disy/ui/icon';
import { icon, iconStack } from '@disy/ui/icon';
import type { MenuContentElement } from '@disy/ui/menu';
import { setupMenuButton } from '@disy/ui/menu';
import { setupPopupButton } from '@disy/ui/popup';

import { createResourceLabel } from 'ui/resource-label/resource-label';

import type { HotkeyId } from 'cadenza/hotkeys/hotkey-definitions';
import { bindHotkey, getFormattedHotkey, isHotkeyBound, unbindHotkey } from 'cadenza/hotkeys/hotkeys';
import { projectKindToResourceType } from 'cadenza/project/project-resource-label';
import { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { ProjectKind } from 'cadenza/repository/model/project-kind.gen';
import type { ProjectName } from 'cadenza/repository/model/project-name.gen';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { setProjectId } from 'cadenza/store/cadenza.state';
import { STATUS, observeStore } from 'cadenza/store/state-utils';
import type { SavedState } from 'cadenza/ui/version-tag/version-tag-provider';
import { createSnapshotTag, createVersionTag } from 'cadenza/ui/version-tag/version-tag-provider';
import { navigateWithoutConfirmation } from 'cadenza/utils/confirm-navigation-util';
import type { WorkbookRelation } from 'cadenza/workbook/api/workbook-relation';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import { LiveModeAction } from 'cadenza/workbook/header/live-mode-action/live-mode-action';
import {
  executeSaveAsAction,
  saveAndPublishWorkbookAction,
  saveWorkbookAction,
} from 'cadenza/workbook/header/save-workbook-actions/save-workbook-actions';
import { toggleOperationModeSimplified } from 'cadenza/workbook/header/toggle-simplifed-mode/toggle-simplified-mode';
import { createMoreActionsMenuContent } from 'cadenza/workbook/header/workbook-action-menu-content/workbook-action-menu-content';
import {
  isDownloadWorkbookMenuItemVisible,
  isGenerateReportsWorkbookMenuItemVisible,
  isShareButtonDisabled,
} from 'cadenza/workbook/header/workbook-header-selectors.state';
import { getShareButtonTitle } from 'cadenza/workbook/header/workbook-header-util';
import { createWorksheetNavigation } from 'cadenza/workbook/header/worksheet-navigation';
import {
  canManageWorkbook,
  openManageWorkbookDialog,
} from 'cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog-util';
import type { WorkbookType } from 'cadenza/workbook/model/workbook-type.gen';
import {
  canTheUserSaveWorkbooks,
  getVersionTagAttributes,
  isSaveAndPublishButtonVisible,
  isSaveAsButtonVisible,
  isSaveButtonVisible,
} from 'cadenza/workbook/state/workbook-header';
import { showPanel } from 'cadenza/workbook/state/workbook-panel-actions.state';
import { isPanelDisabled, isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import { openVisualizationPickerModalAndAddView } from 'cadenza/workbook/workbook-add-new-view/workbook-add-new-view';
import {
  areRequiredConditionsValid,
  getActiveWorksheet,
  getInitializationStatus,
  getWorkbookPrintName,
  hasForceMaximizedView,
  isAnalysisContextAffectingViews,
  isWorkbookChanged,
  isWorkbookSaved,
  isWorkbookVisibilityRestricted,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { togglePanelVisibility, updateWorkbookMetadata } from 'cadenza/workbook/workbook.state';

import './workbook-header.css';
import i18n from './workbook-header.properties';

const COMPONENT_NAME = 'd-workbook-header';
const MORE_ACTIONS_HIDDEN_CLASS = 'more-actions-hidden';

export class WorkbookHeader extends HTMLElement {
  #teardowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-legacy-toolbar');
    this.setAttribute('role', 'navigation');
    this.dataset.testid = 'workbook-header';
    this.#updateStoreFromPebbleTemplateAttributes();
    if (window.location.hash === '#drill-through-management') {
      this.#openDrillThroughOptionsOnInitCompleted();
    }
    this.#teardowns.push(on(currentWindowMediaXsOnly, 'change', () => this.#handleAnalysisContextToggleHotkey()));
    this.#renderContentOnInitCompleted();
  }

  #openDrillThroughOptionsOnInitCompleted() {
    observeStore(getStore(), getInitializationStatus, (initializationStatus) => {
      if (initializationStatus === STATUS.SUCCEEDED) {
        openManageWorkbookDialog('drillThroughTargets').finally(() => {
          // remove hash from url
          history.pushState('', document.title, window.location.pathname + window.location.search);
        });
      }
    });
  }

  #renderContentOnInitCompleted() {
    const unsubscribeInit = observeStore(getStore(), getInitializationStatus, (initializationStatus) => {
      if (initializationStatus === 'succeeded') {
        unsubscribeInit();
        this.#initHeader();
      }
    });
  }

  #updateStoreFromPebbleTemplateAttributes() {
    getStore().dispatch(
      updateWorkbookMetadata({
        clientChanged: this.getAttribute('client-changed') === 'true',
      }),
    );

    if (this.#projectId) {
      getStore().dispatch(setProjectId(this.#projectId));
    }
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  get #projectId() {
    const projectName = this.getAttribute('project-name') as ProjectName;
    if (projectName) {
      const repositoryName = this.getAttribute('repository-name') as RepositoryName;
      return ProjectId.of(repositoryName, projectName);
    }
  }

  #renderLeftPanel() {
    return h(`.${COMPONENT_NAME}--left-tools`, [
      ...this.#createAnalysisContextButtonAndDivider(),
      ...this.#createProjectButtonAndDivider(),
      this.#createVersionTag(),
      createWorkbookWithWorksheetTitle(),
      createWorksheetNavigation(this.#teardowns),
      new LiveModeAction(),
    ]);
  }

  #createVersionTag() {
    let tag = createTag(getVersionTagAttributes(getStore().getState())) || h('.d-tag.hidden');
    this.#teardowns.push(
      observeStore(
        getStore(),
        getVersionTagAttributes,
        (versionTagAttributes) => {
          if (!versionTagAttributes.visible) {
            tag!.hidden = true;
          } else {
            const newTag = createTag(versionTagAttributes);
            if (newTag) {
              tag!.replaceWith(newTag);
              tag = newTag;
            }
          }
        },
        true,
      ),
    );
    return tag;
  }

  #createAnalysisContextButtonAndDivider() {
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context')) {
      return [];
    }
    if (
      !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context-closeable') &&
      !currentWindowMediaXsOnly.matches
    ) {
      getStore().dispatch(showPanel(WorkbookPanelName.ANALYSIS_CONTEXT, { highlight: false }));
    }
    return [this.#createAnalysisContextToggleButton(), h(`.${COMPONENT_NAME}--divider-large`)];
  }

  #createAnalysisContextToggleButton() {
    const hotkeyId = 'WORKBOOK_TOGGLE_ANALYSIS_CONTEXT';
    const button = createToggleButton(WorkbookPanelName.ANALYSIS_CONTEXT, getAnalysisContextIcon(), hotkeyId);
    this.#teardowns.push(
      ...subscribePanelToggleButton(button, WorkbookPanelName.ANALYSIS_CONTEXT),
      observeStore(getStore(), isAnalysisContextAffectingViews, (state) => {
        button.firstElementChild!.replaceWith(getAnalysisContextIcon(state));
        const messageKey = `d-analysis-context-panel.toggle${state ? '.hasActiveFilters' : ''}`;
        updateButtonTitle(button, i18n(messageKey, { hotkey: getFormattedHotkey(hotkeyId) }));

        if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context-closeable')) {
          button.disabled = true;
        }
      }),
    );
    return button;
  }

  #createProjectButtonAndDivider() {
    const projectName = this.getAttribute('project-name');
    const projectPrintName = this.getAttribute('project-print-name');
    if (projectName && projectPrintName) {
      const resourceType = projectKindToResourceType(this.getAttribute('project-kind') as ProjectKind);
      const repositoryName = this.getAttribute('repository-name') as RepositoryName;
      const projectUrl = cadenzaUrl(`/repositories/${repositoryName}/projects/${projectName}`);
      const button = createButton(
        createResourceLabel(resourceType, h(`span.hidden-xs`, projectPrintName), {
          styleClass: 'ellipsis',
        }),
        {
          onclick: () => navigateWithoutConfirmation(projectUrl),
          variant: 'link',
          size: 'm',
          styleClass: 'hidden-xs',
        },
      );
      return [button, h(`.${COMPONENT_NAME}--divider-small.hidden-xs`)];
    } else {
      return [];
    }
  }

  #createDesignerToggleButton() {
    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-design')) {
      const designerToggleButton = createToggleButton(
        WorkbookPanelName.VIEW_SETTINGS,
        icon(designerIcon),
        'WORKBOOK_TOGGLE_VIEW_SETTINGS',
      );
      this.#teardowns.push(
        ...subscribePanelToggleButton(designerToggleButton, WorkbookPanelName.VIEW_SETTINGS),
        observeStore(getStore(), areRequiredConditionsValid, (requiredConditionsValid) => {
          designerToggleButton!.disabled = !requiredConditionsValid;
        }),
      );
      return designerToggleButton;
    }
  }

  #createDataBrowserToggleButton() {
    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-browser')) {
      const dataBrowserToggleButton = createToggleButton(
        WorkbookPanelName.DATA_BROWSER,
        icon(dataBrowserIcon),
        'WORKBOOK_TOGGLE_DATA_BROWSER',
      );
      this.#teardowns.push(
        ...subscribePanelToggleButton(dataBrowserToggleButton, WorkbookPanelName.DATA_BROWSER),
        observeStore(getStore(), areRequiredConditionsValid, (requiredConditionsValid) => {
          dataBrowserToggleButton.disabled = !requiredConditionsValid;
        }),
      );
      return dataBrowserToggleButton;
    }
  }

  #createAddNewViewButton() {
    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-new-view')) {
      const addNewViewButton = createAddNewViewButton();
      this.#teardowns.push(
        observeStore(getStore(), hasForceMaximizedView, (disabled) => {
          addNewViewButton.disabled = disabled;
        }),
      );
      return addNewViewButton;
    }
  }

  #createSaveButton() {
    const saveButton = this.#createSaveWorkbookButton();
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => ({
          disabled: hasForceMaximizedView(state) || !isWorkbookChanged(state),
          visible: isSaveButtonVisible(state),
        }),
        ({ disabled, visible }) => {
          saveButton.disabled = disabled;
          saveButton.hidden = !visible;
        },
        true,
      ),
    );
    return saveButton;
  }

  #createSaveAsButton() {
    const saveAsButton = this.#createSaveAsWorkbookButton();
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => ({
          disabled: hasForceMaximizedView(state),
          visible: isSaveAsButtonVisible(state),
        }),
        ({ disabled, visible }) => {
          saveAsButton.disabled = disabled;
          saveAsButton.hidden = !visible;
        },
        true,
      ),
    );
    return saveAsButton;
  }

  #createSaveAndPublishButton() {
    const saveAndPublishButton = createButton(
      [
        icon(saveIcon),
        h('span.d-workbook-header--main-actions--label', i18n('save-and-publish-workbook.button.title')),
      ],
      {
        variant: 'borderless',
        title: i18n('save-and-publish-workbook.button.title'),
        onclick: () => saveAndPublishWorkbookAction(),
        styleClass: 'hidden-xs',
      },
    );
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => ({
          disabled: hasForceMaximizedView(state) || !isWorkbookChanged(getStore().getState()),
          visible: isSaveAndPublishButtonVisible(state),
        }),
        ({ disabled, visible }) => {
          saveAndPublishButton.disabled = disabled;
          saveAndPublishButton.hidden = !visible;
        },
        true,
      ),
    );
    return saveAndPublishButton;
  }

  #createAndSetupShareMenuButton() {
    const shareMenuButton = this.#createShareMenuButton();
    setupPopupButton(
      shareMenuButton,
      async () => {
        const { createShareMenuContent } =
          await import('cadenza/workbook/header/share-popup-content/share-popup-content');
        return [createShareMenuContent()];
      },
      { placement: 'bottom-end', styleClass: 'd-menu' },
    );
    return shareMenuButton;
  }

  #renderRightPanel() {
    const rightToolsContainer = h(`.${COMPONENT_NAME}--right-tools`, [
      h(`.${COMPONENT_NAME}--main-actions.d-stack-h`, [
        this.#createSaveAsButton(),
        this.#createSaveButton(),
        this.#createSaveAndPublishButton(),
        this.#createAndSetupShareMenuButton(),
        this.#createAddNewViewButton(),
        this.#createMoreActionsButton(),
      ]),
    ]);
    const designerToggleButton = this.#createDesignerToggleButton();
    const dataBrowserToggleButton = this.#createDataBrowserToggleButton();
    if (designerToggleButton || dataBrowserToggleButton) {
      rightToolsContainer.append(h(`.${COMPONENT_NAME}--divider-large`));
      if (designerToggleButton) {
        rightToolsContainer.append(designerToggleButton);
      }
      if (dataBrowserToggleButton) {
        rightToolsContainer.append(dataBrowserToggleButton);
      }
    }
    return rightToolsContainer;
  }

  #initHeader() {
    this.append(this.#renderLeftPanel(), this.#renderRightPanel());

    this.#handleAnalysisContextToggleHotkey();

    if (canManageWorkbook(getStore().getState())) {
      bindHotkey({
        hotkeyId: 'WORKBOOK_OPEN_MANAGE_WORKBOOK',
        callback: () => openManageWorkbookDialogIfNoModalOpen(),
      });
    }

    bindHotkey({
      hotkeyId: 'WORKBOOK_TOGGLE_OPERATION_MODE_SIMPLIFIED',
      callback: () => toggleOperationModeSimplified(true),
    });

    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-design')) {
      bindHotkey({
        hotkeyId: 'WORKBOOK_TOGGLE_VIEW_SETTINGS',
        callback: () => getStore().dispatch(togglePanelVisibility(WorkbookPanelName.VIEW_SETTINGS)),
      });
    }

    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-browser')) {
      bindHotkey({
        hotkeyId: 'WORKBOOK_TOGGLE_DATA_BROWSER',
        callback: () => getStore().dispatch(togglePanelVisibility(WorkbookPanelName.DATA_BROWSER)),
      });
    }

    observeStore(getStore(), isWorkbookSaved, (isSaved: boolean) => {
      this.setAttribute('saved', String(isSaved));
    });
    observeStore(getStore(), isWorkbookChanged, (isChanged: boolean) => {
      this.setAttribute('client-changed', String(isChanged));
    });
  }

  #handleAnalysisContextToggleHotkey() {
    const hotkeyId = 'WORKBOOK_TOGGLE_ANALYSIS_CONTEXT';
    if (
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context') &&
      (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context-closeable') ||
        currentWindowMediaXsOnly.matches)
    ) {
      if (isHotkeyBound(hotkeyId)) {
        return;
      }
      bindHotkey({
        hotkeyId,
        callback: () => getStore().dispatch(togglePanelVisibility(WorkbookPanelName.ANALYSIS_CONTEXT)),
      });
    } else {
      unbindHotkey(hotkeyId);
    }
  }

  #createSaveAsWorkbookButton() {
    return createButton(
      [icon(saveIcon), h('span.d-workbook-header--main-actions--label', i18n('save-as-workbook.button.title'))],
      {
        variant: 'borderless',
        title: i18n('save-as-workbook.button.title'),
        onclick: () => executeSaveAsAction(),
      },
    );
  }

  #createSaveWorkbookButton() {
    return createButton(
      [icon(saveIcon), h('span.d-workbook-header--main-actions--label', i18n('save-workbook.button.title'))],
      {
        variant: 'borderless',
        title: i18n('save-workbook.button.title'),
        onclick: () => saveWorkbookAction(),
        styleClass: ['hidden-xs', 'workbook-save'],
      },
    );
  }

  #createMoreActionsButton() {
    const moreActionsButton = createButton(
      [icon(more), h('span.d-workbook-header--main-actions--label', i18n('more-actions-view-menu.button.label'))],
      {
        variant: 'borderless',
        title: i18n('more-actions-view-menu.button.label'),
      },
    );
    setupMenuButton(moreActionsButton, () => [...this.#createMoreActions()], { styleClass: 'more-actions-popup-menu' });
    this.#teardowns.push(
      observeStore(getStore(), hasForceMaximizedView, (disabled) => {
        moreActionsButton.disabled = disabled;
      }),
      // Instead of refreshing the actions every time something changes in workbook, we do it only
      // if some state changed that affects what actions are visible. Ideally, we should create
      // actions based on state and observe if the actions themselves changed - but it's too complicated at
      // the moment. Another option to solve this would be to get the menu items themselves (elements)
      // and set hidden on them instead of recreating them every time. But setupMenuButton doesn't allow that.
      observeStore(
        getStore(),
        (state) => ({
          // List all state that affects what actions are created in createMoreActionsMenuContent
          visibilityRestricted: isWorkbookVisibilityRestricted(state),
          isWorkbookSaved: isWorkbookSaved(state),
          canTheUserSaveWorkbooks: canTheUserSaveWorkbooks(state),
          isWorkbookManagementAvailable: isWorkbookFeatureAvailable(state, 'workbook-management'),
          isWorkbookManagementDialogAvailable: canManageWorkbook(getStore().getState()),
          isDownloadWorkbookMenuItemVisible: isDownloadWorkbookMenuItemVisible(state),
          isGenerateReportsWorkbookMenuItemVisible: isGenerateReportsWorkbookMenuItemVisible(state),
        }),
        () => {
          moreActionsButton.classList.toggle(
            MORE_ACTIONS_HIDDEN_CLASS,
            !shouldShowMoreActionsButtonOnlyOnSmallDisplay(this.#createMoreActions()),
          );
        },
        true, // Compare by value = true
      ),
    );
    return moreActionsButton;
  }

  #createMoreActions() {
    return createMoreActionsMenuContent(executeSaveAsAction);
  }

  #createShareMenuButton() {
    const shareMenuButton = createButton(
      [icon(shareIcon), h('span.d-workbook-header--main-actions--label', i18n('share.button'))],
      {
        variant: 'borderless',
        title: getShareButtonTitle(),
        styleClass: 'hidden-xs',
      },
    );
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => isShareButtonDisabled(state),
        (disabled) => {
          shareMenuButton.disabled = disabled;
          shareMenuButton.title = getShareButtonTitle();
        },
      ),
    );
    return shareMenuButton;
  }
}

customElements.define(COMPONENT_NAME, WorkbookHeader);

function openManageWorkbookDialogIfNoModalOpen() {
  // don't show manage-workbook-dialog if another modal is open
  if (document.querySelector('.d-modal')) {
    return;
  }
  openManageWorkbookDialog();
}

export function getAnalysisContextIcon(state = false) {
  return state ?
      iconStack([{ icon: analysisContextIcon }, { icon: analysisContextBubbleIcon }], {
        styleClass: 'd-analysis-context-icon',
      })
    : icon(analysisContextIcon);
}

function shouldShowMoreActionsButtonOnlyOnSmallDisplay(standardActions: MenuContentElement[]) {
  return !!standardActions.length && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-more-actions');
}

function createWorkbookTitle() {
  const titleSpan = h('span.workbook-title.hidden-xs.ellipsis');
  observeStore(getStore(), getWorkbookPrintName, (workbookName) => {
    titleSpan.textContent = workbookName;
  });
  return titleSpan;
}

function createWorksheetPrintName() {
  const activeWorksheet = getActiveWorksheet(getStore().getState());
  const worksheetPrintNameSpan = h(`span.worksheet-name.ellipsis`, getWorksheetPrintName(activeWorksheet));
  observeStore(getStore(), getActiveWorksheet, (worksheet?: WorksheetDto) => {
    worksheetPrintNameSpan.textContent = getWorksheetPrintName(worksheet);
  });
  worksheetPrintNameSpan.classList.toggle('.worksheet-name-error', !activeWorksheet);
  return worksheetPrintNameSpan;
}

function getWorksheetPrintName(activeWorksheet?: WorksheetDto) {
  return activeWorksheet?.printName ?? i18n('worksheet.not.available.label');
}

function createWorkbookWithWorksheetTitle() {
  return h('h2.workbook-worksheet-heading', [
    createWorkbookTitle(),
    h(`.${COMPONENT_NAME}--divider-small.hidden-xs`),
    createWorksheetPrintName(),
  ]);
}

export interface VersionTagAttributes {
  workbookType: WorkbookType;
  workbookVersion?: WorkbookVersion;
  workbookVersionMode: PublicationStage | SavedState;
  workbookRelation?: WorkbookRelation;
  visible: boolean;
  disabled: boolean;
  modified: boolean;
}

function createTag(versionTagAttributes: VersionTagAttributes) {
  const { workbookType, workbookRelation } = versionTagAttributes;
  switch (workbookType) {
    case 'WORKBOOK':
    case 'TRANSIENT':
      return createVersionTagButton(versionTagAttributes);
    case 'SNAPSHOT':
      return createSnapshotTag(workbookRelation);
    default:
      throw new Error('Type ' + workbookType + ' is not mapped, yet');
  }
}

function createVersionTagButton(versionTagAttributes: VersionTagAttributes) {
  const versionMode = versionTagAttributes.workbookVersionMode;
  const modified = versionTagAttributes.modified;
  // CADENZA-40470: Only "published" and "draft" tags should be shown without any version number
  // So "saved" and "unsaved" states need to return no version tag button
  if (['unsaved', 'saved'].includes(versionMode) || modified) {
    return;
  }
  return createVersionTag(versionMode, {
    disabled: versionTagAttributes.disabled,
    modified,
    short: true,
    onclick: () => openManageWorkbookDialog('versions'),
  });
}

function createToggleButton(panelId: WorkbookPanelName, svgIcon: Icon, hotkey: HotkeyId) {
  return createIconButton(svgIcon, i18n(`${panelId}.toggle`, { hotkey: getFormattedHotkey(hotkey) }), {
    variant: 'borderless',
    pressed: isPanelVisible(getStore().getState(), panelId),
    attrs: { 'aria-controls': panelId },
    onclick: () => getStore().dispatch(togglePanelVisibility(panelId)),
  });
}

function subscribePanelToggleButton(button: HTMLButtonElement & { pressed?: boolean }, panelId: WorkbookPanelName) {
  const subscriptions: Teardown[] = [];
  subscriptions.push(
    observeStore(
      getStore(),
      (state) => isPanelVisible(state, panelId),
      (visible) => {
        button.pressed = visible;
        if (!visible) {
          button.focus();
        }
      },
    ),
  );
  subscriptions.push(
    observeStore(
      getStore(),
      (state) => isPanelDisabled(state, panelId),
      (disabled) => {
        button.disabled = disabled;
      },
    ),
  );
  return subscriptions;
}

function createAddNewViewButton() {
  const button = createButton(
    [icon(addViewIcon), h('span.d-workbook-header--main-actions--label', i18n('add-view-menu.button.title'))],
    {
      variant: 'borderless',
      title: i18n('add-view-menu.button.title'),
      styleClass: 'hidden-xs',
      onclick: () => openVisualizationPickerModalAndAddView(),
    },
  );
  button.dataset.testid = 'workbook-create-new-view-button';
  return button;
}
